import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last Updated: September 2026",
    intro:
      "VeraChart (\u201cwe,\u201d \u201cour,\u201d or \u201cus\u201d) respects your privacy. Because of the nature of our business model, we do not host a software application that processes clinical data. This Privacy Policy explains what limited information we collect from our clients and website visitors.",
    sections: [
      {
        h: "1. Information We Collect",
        paras: ["We only collect the information necessary to conduct business with you, provide consulting services, and process payments. This includes:"],
        bullets: [
          "Contact Information: Name, agency name, email address, and phone number.",
          "Billing Information: Payment details processed securely through our third-party payment processors.",
          "Communication Data: Records of emails, Zoom/Teams meetings, and correspondence regarding your prompt customization needs.",
        ],
      },
      {
        h: "2. What We Do NOT Collect (Clinical Data)",
        paras: [
          "VeraChart does not collect, process, or store Protected Health Information (PHI) or client notes. Our business involves providing you with the text prompts to use in your own secure systems. You should never send us patient records, completed clinical notes, or unredacted medical information during our consulting sessions or via email.",
        ],
      },
      {
        h: "3. How We Use Your Information",
        paras: ["We use your information solely to:"],
        bullets: [
          "Deliver the purchased prompt templates and customized digital products.",
          "Schedule and conduct onboarding and consulting sessions.",
          "Process payments and send invoices.",
          "Communicate updates about our services.",
        ],
      },
      {
        h: "4. Data Sharing",
        paras: [
          "We do not sell, rent, or trade your personal or agency information to third parties. We only share information with trusted third-party service providers (such as payment processors or video conferencing tools) strictly to operate our business.",
        ],
      },
      {
        h: "5. Contact Us",
        paras: [
          "If you have questions about how we handle your business information, please contact us at: bcooper@verachart.org or sales@verachart.org",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    updated: "Last Updated: September 2026",
    intro:
      "Welcome to VeraChart. These Terms of Use (\u201cTerms\u201d) govern your purchase and use of VeraChart\u2019s prompt engineering templates, digital products, and consulting services (collectively, the \u201cServices\u201d). By purchasing or utilizing our Services, you agree to these Terms.",
    sections: [
      {
        h: "1. Description of Services",
        paras: [
          "VeraChart provides customized AI prompt templates, prompt libraries, and related onboarding training (via Zoom, Google Meet, or Microsoft Teams) designed to assist healthcare workers and agencies in generating a variety of clinical documentation using third-party Artificial Intelligence (AI) platforms. This includes, but is not limited to, prompts for Progress Notes, Visit Attempts, Collateral Contacts, Crisis Call logs, Person-Centered Plan (PCP) details, and Comprehensive Clinical Assessments (CCA).",
        ],
      },
      {
        h: "2. Third-Party AI Platforms and HIPAA Compliance",
        paras: [
          "VeraChart is not an AI software platform, an Electronic Health Record (EHR), or a data processor. You are responsible for selecting, vetting, and maintaining the AI platform (e.g., ChatGPT, Microsoft Copilot, Google Workspace) where you utilize our prompts.",
        ],
        bullets: [
          "Your Responsibility: It is strictly your agency\u2019s responsibility to ensure that the AI platform you use complies with the Health Insurance Portability and Accountability Act (HIPAA), including securing the necessary Business Associate Agreements (BAAs) with the AI provider.",
          "De-Identification: You are solely responsible for ensuring that your staff follows best practices regarding de-identification and the omission of Protected Health Information (PHI) when interacting with any AI platform, regardless of the prompts provided by VeraChart.",
        ],
      },
      {
        h: "3. Intellectual Property and Licensing",
        paras: [
          "When you purchase prompt templates from VeraChart, you are purchasing a non-exclusive, non-transferable license to use these prompts internally within your specific agency or team.",
        ],
        bullets: [
          "You may distribute these prompts to your direct employees and team members.",
          "You may not resell, publicly share, publish, or distribute VeraChart\u2019s prompts to outside agencies, independent contractors outside your organization, or the general public.",
        ],
      },
      {
        h: "4. Consulting, Revisions, and Future Services",
        paras: [
          "The initial purchase of a VeraChart prompt package includes an initial setup and walkthrough consultation (typically 1\u20132 hours).",
        ],
        bullets: [
          "Future Training: Additional onboarding for new staff members, follow-up training sessions, or future modifications to the prompts requested after the finalization of the initial setup will be billed separately at our standard consulting or upgrade rates.",
        ],
      },
      {
        h: "5. Limitation of Liability",
        paras: [
          "VeraChart provides prompt templates to optimize your documentation workflow. We are not liable for the clinical accuracy, billing compliance, or legal adequacy of the final documentation generated by your chosen AI using our prompts. You and your agency are solely responsible for reviewing and verifying all generated notes before submitting them to official medical records. VeraChart shall not be liable for any data breaches, HIPAA violations, or errors caused by your chosen third-party AI software.",
        ],
      },
      {
        h: "6. Payments and Refunds",
        paras: [
          "Because our Services consist of intellectual property (digital templates) and specialized consulting time, all sales are final and non-refundable once the prompts have been delivered or the initial consultation has commenced.",
        ],
      },
      {
        h: "7. Team Licensing",
        paras: [
          "A VeraChart Team License authorizes use of the licensed VeraChart materials by the specifically identified team within the purchasing organization. Additional teams require separate licenses unless otherwise provided under a written multi-team or enterprise agreement.",
          "License credentials and licensed materials may not be transferred to another team or organization without authorization from VeraChart. Each team's delivered materials carry team-specific license identification so unauthorized redistribution can be identified.",
        ],
      },
    ],
  },
};

export default function Legal({ kind }) {
  const c = CONTENT[kind] || CONTENT.privacy;

  useEffect(() => {
    document.title = `${c.title} | VeraChart`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "VeraChart | AI Prompts for Behavioral Health Documentation";
    };
  }, [c.title]);

  return (
    <div className="min-h-screen bg-white font-body text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to VeraChart
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid={`legal-${kind}-page`}>
        <h1 className="font-display text-4xl font-bold tracking-tight text-navy">{c.title}</h1>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">{c.updated}</p>
        <p className="mt-8 text-base leading-relaxed text-slate-600 sm:text-lg">{c.intro}</p>
        {c.sections.map((s) => (
          <section key={s.h} className="mt-10">
            <h2 className="font-display text-xl font-bold text-navy">{s.h}</h2>
            {s.paras.map((p, i) => (
              <p key={i} className="mt-3 text-base leading-relaxed text-slate-600">{p}</p>
            ))}
            {s.bullets && (
              <ul className="mt-3 list-disc space-y-2.5 pl-6 text-base leading-relaxed text-slate-600">
                {s.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </main>
      <footer className="border-t border-slate-200 py-8">
        <p className="mx-auto max-w-3xl px-4 text-xs text-slate-400 sm:px-6 lg:px-8">
          © 2026 VeraChart. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
